import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MagnifyingGlass, House, Star, Trophy, X, SignIn, GearSix, SignOut, User, Sun, Moon } from "@phosphor-icons/react";
import { useFavorites } from "../contexts/FavoritesContext";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import PreferencesModal from "./PreferencesModal";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAGUE_LINKS = [
  { key: "nfl", name: "NFL" },
  { key: "nba", name: "NBA" },
  { key: "mlb", name: "MLB" },
  { key: "nhl", name: "NHL" },
  { key: "epl", name: "EPL" },
  { key: "mls", name: "MLS" },
  { key: "pga", name: "GOLF" },
  { key: "nascar", name: "NASCAR" },
  { key: "ufc", name: "UFC" },
  { key: "ncaaf", name: "NCAAF" },
  { key: "ncaab", name: "NCAAB" },
];

export default function Header() {
  const navigate = useNavigate();
  const { favorites } = useFavorites();
  const { user, login, logout, loading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const searchRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const resp = await axios.get(`${API}/search`, { params: { q: searchQuery } });
        setSearchResults(resp.data.results || []);
      } catch (err) {
        console.error("Search error:", err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
    <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10" data-testid="main-header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        {/* Top row */}
        <div className="relative flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2 group" data-testid="home-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_scoreboard-stream/artifacts/a82wzpdx_Copilot_20260412_215512.png" 
              alt="Guy's Total Sports Center" 
              className="h-11 w-auto object-contain"
            />
            <span className="font-['Bebas_Neue'] text-lg tracking-wider text-white hidden sm:inline">
              GUY'S TOTAL SPORTS CENTER
            </span>
          </Link>

          {/* Guy's MotorSports logo - centered, opens external site in new tab */}
          <a
            href="https://guysmotorsports.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto hidden md:flex items-center justify-center group"
            data-testid="motorsports-logo-link"
            title="Visit Guy's MotorSports"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_scoreboard-stream/artifacts/p4bc406d_full.png"
              alt="Guy's MotorSports - EST 1977"
              className="h-20 w-auto object-contain opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-200 drop-shadow-[0_0_12px_rgba(251,191,36,0.25)]"
            />
          </a>

          <div className="flex items-center gap-3">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              data-testid="theme-toggle"
            >
              {theme === "dark" ? <Sun size={18} className="text-white/70" /> : <Moon size={18} className="text-white/70" />}
            </button>

            {/* Search */}
            <div className="relative" ref={searchRef}>
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                data-testid="search-toggle-btn"
              >
                <MagnifyingGlass size={18} className="text-white/70" />
              </button>

              {searchOpen && (
                <div className="absolute right-0 top-12 w-80 bg-[#141414] border border-white/10 rounded-lg shadow-2xl overflow-hidden animate-fade-in" data-testid="search-dropdown">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
                    <MagnifyingGlass size={16} className="text-white/40" />
                    <input
                      type="text"
                      placeholder="Search teams..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/30"
                      data-testid="search-input"
                      autoFocus
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery("")} data-testid="search-clear-btn">
                        <X size={14} className="text-white/40" />
                      </button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {searching && <p className="px-3 py-2 text-xs text-white/40">Searching...</p>}
                    {searchResults.map((r) => (
                      <button
                        key={`${r.league}-${r.id}`}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors text-left"
                        data-testid={`search-result-${r.id}`}
                        onClick={() => {
                          navigate(`/team/${r.league}/${r.id}`);
                          setSearchOpen(false);
                          setSearchQuery("");
                        }}
                      >
                        {r.logo && <img src={r.logo} alt="" className="w-6 h-6 object-contain" />}
                        <div>
                          <p className="text-sm text-white">{r.name}</p>
                          <p className="text-xs text-white/40">{r.leagueName}</p>
                        </div>
                      </button>
                    ))}
                    {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                      <p className="px-3 py-3 text-xs text-white/40 text-center">No results found</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Favorites count */}
            <Link
              to="/?tab=favorites"
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors relative"
              data-testid="favorites-link"
            >
              <Star size={18} weight={favorites.length > 0 ? "fill" : "regular"} className="text-[#007AFF]" />
              {favorites.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#007AFF] text-[9px] flex items-center justify-center text-white font-bold">
                  {favorites.length}
                </span>
              )}
            </Link>

            {/* Auth */}
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-1 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  data-testid="user-menu-btn"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#007AFF] flex items-center justify-center text-[10px] font-bold text-white">
                      {user.name?.charAt(0) || "U"}
                    </div>
                  )}
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-11 w-52 bg-[#141414] border border-white/10 rounded-lg shadow-2xl overflow-hidden animate-fade-in z-50" data-testid="user-menu-dropdown">
                    <div className="px-3 py-2.5 border-b border-white/10">
                      <p className="text-xs text-white font-medium truncate">{user.name}</p>
                      <p className="text-[10px] text-white/40 truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => { setPrefsOpen(true); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/70 hover:bg-white/5 transition-colors"
                      data-testid="preferences-btn"
                    >
                      <GearSix size={14} /> Favorite Teams & Sports
                    </button>
                    <button
                      onClick={() => { logout(); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/70 hover:bg-white/5 transition-colors border-t border-white/5"
                      data-testid="logout-btn"
                    >
                      <SignOut size={14} /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={login}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#007AFF] hover:bg-[#007AFF]/80 transition-colors text-white text-xs font-semibold tracking-wider"
                data-testid="login-btn"
              >
                <SignIn size={14} /> Sign In
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
    <PreferencesModal isOpen={prefsOpen} onClose={() => setPrefsOpen(false)} />
    </>
  );
}
